import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { supabase } from '../../utils/supabaseClient';
import styles from './Login.module.css';
import { 
  FiMail, FiLock, FiArrowRight, FiEye, FiEyeOff, 
  FiAlertCircle, FiCheckCircle, FiLogIn
} from 'react-icons/fi';
import bvLogo from '../../assets/bv.jpg';

const Login = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const { data, error: signInError } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password: password
      });

      if (signInError) throw signInError;

      if (data.user) {
        // Get user profile from custom table
        const { data: profile, error: profileError } = await supabase
          .from('user_profiles')
          .select('*')
          .eq('id', data.user.id)
          .single();

        if (profileError && profileError.code !== 'PGRST116') {
          console.error('Profile fetch error:', profileError);
        }

        setSuccess('Login successful! Redirecting...');
        setTimeout(() => {
          navigate('/');
        }, 1500);
      }
    } catch (err) {
      setError(err.message || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = () => {
    navigate('/forgot-password');
  };

  return (
    <div className={styles.loginPage}>
      <div className={styles.container}>
        {/* Left Side - Branding */}
        <div className={styles.brandSection}>
          <div className={styles.brandContent}>
            <motion.div 
              className={styles.logoWrapper}
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <img src={bvLogo} alt="BrandVerse Logo" className={styles.brandLogo} />
            </motion.div>
            
            <motion.h1 
              className={styles.brandTitle}
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              Welcome Back!
            </motion.h1>
            
            <motion.p 
              className={styles.brandSubtitle}
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              Sign in to access your dashboard and manage your projects
            </motion.p>
            
            <motion.div 
              className={styles.featuresList}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <div className={styles.featureItem}>
                <FiCheckCircle />
                <span>Access your project dashboard</span>
              </div>
              <div className={styles.featureItem}>
                <FiCheckCircle />
                <span>Track your service requests</span>
              </div>
              <div className={styles.featureItem}>
                <FiCheckCircle />
                <span>Get real-time updates</span>
              </div>
              <div className={styles.featureItem}>
                <FiCheckCircle />
                <span>Connect with support team</span>
              </div>
            </motion.div>
          </div>
          
          <div className={styles.brandFooter}>
            <p>New to BrandVerse?</p>
            <Link to="/signup" className={styles.signupLink}>
              Create an account <FiArrowRight />
            </Link>
          </div>
        </div>

        {/* Right Side - Login Form */}
        <div className={styles.formSection}>
          <motion.div 
            className={styles.formContainer}
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className={styles.formHeader}>
              <h2>Sign In</h2>
              <p>Enter your credentials to access your account</p>
            </div>

            {error && (
              <motion.div 
                className={styles.errorMessage}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <FiAlertCircle />
                <span>{error}</span>
              </motion.div>
            )}

            {success && (
              <motion.div 
                className={styles.successMessage}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <FiCheckCircle />
                <span>{success}</span>
              </motion.div>
            )}

            <form onSubmit={handleLogin} className={styles.loginForm}>
              <div className={styles.inputGroup}>
                <label htmlFor="email">Email Address</label>
                <div className={styles.inputWrapper}>
                  <FiMail className={styles.inputIcon} />
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    required
                    disabled={loading}
                  />
                </div>
              </div>

              <div className={styles.inputGroup}>
                <label htmlFor="password">Password</label>
                <div className={styles.inputWrapper}>
                  <FiLock className={styles.inputIcon} />
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    required
                    disabled={loading}
                  />
                  <button
                    type="button"
                    className={styles.passwordToggle}
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <FiEyeOff /> : <FiEye />}
                  </button>
                </div>
              </div>

              <div className={styles.formOptions}>
                <label className={styles.rememberMe}>
                  <input type="checkbox" />
                  <span>Remember me</span>
                </label>
                <button 
                  type="button" 
                  className={styles.forgotPassword}
                  onClick={handleForgotPassword}
                >
                  Forgot password?
                </button>
              </div>

              <button 
                type="submit" 
                className={styles.loginButton}
                disabled={loading}
              >
                {loading ? (
                  <div className={styles.spinner}></div>
                ) : (
                  <>
                    Sign In <FiLogIn />
                  </>
                )}
              </button>
            </form>

            <div className={styles.formFooter}>
              <p>
                Don't have an account?{' '}
                <Link to="/signup" className={styles.registerLink}>
                  Sign up now
                </Link>
              </p>
            </div>

            <div className={styles.divider}>
              <span>Or continue with</span>
            </div>

            <div className={styles.socialButtons}>
              <button className={styles.socialButton} disabled>
                Google
              </button>
              <button className={styles.socialButton} disabled>
                GitHub
              </button>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Login;